import { Injectable } from '@angular/core';
import { Subject, map, retry, switchMap } from 'rxjs';
import { UploadServiceService } from '../service/upload-service.service';
import { environment } from '../../environments/environment';

export const ASSESSMENT_CAPTURE_MINUTES = [1, 6, 11, 16, 21, 26, 31, 36, 41, 46, 51, 56];

export function buildAssessmentCaptureObjectBase(
  userId: string,
  sessionId: string,
  captureNumber: number,
  minute: number,
  capturedAt: Date
): string {
  const safeSegment = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '_');
  const timestamp = capturedAt.toISOString().replace(/[:.]/g, '-');
  return [
    'assessment-attempt',
    safeSegment(userId),
    safeSegment(sessionId),
    `image_${String(captureNumber).padStart(2, '0')}_minute_${String(minute).padStart(2, '0')}_${timestamp}`
  ].join('/');
}

@Injectable({ providedIn: 'root' })
export class AssessmentProctoringService {
  readonly uploadFailed$ = new Subject<unknown>();
  readonly cameraStopped$ = new Subject<void>();

  private stream?: MediaStream;
  private video?: HTMLVideoElement;
  private captureTimeouts: number[] = [];
  private intentionallyStopping = false;

  constructor(private uploadService: UploadServiceService) {}

  get isCameraActive(): boolean {
    return !!this.stream?.getVideoTracks().some(track => track.readyState === 'live');
  }

  async requestCamera(video: HTMLVideoElement): Promise<void> {
    if (this.isCameraActive) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Camera access is not supported by this browser.');
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    });

    this.intentionallyStopping = false;
    this.stream = stream;
    this.video = video;
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;

    for (const track of stream.getVideoTracks()) {
      track.addEventListener('ended', () => {
        if (!this.intentionallyStopping) {
          this.clearCaptureSchedule();
          this.cameraStopped$.next();
        }
      }, { once: true });
    }

    await video.play();
  }

  startCaptureSchedule(userId: string, sessionId: string, assessmentStartedAt: number): void {
    this.clearCaptureSchedule();
    ASSESSMENT_CAPTURE_MINUTES.forEach((minute, index) => {
      const delay = assessmentStartedAt + (minute * 60_000) - Date.now();
      if (delay <= 0) return;
      const timeout = window.setTimeout(
        () => void this.captureAndUpload(userId, sessionId, index + 1, minute),
        delay
      );
      this.captureTimeouts.push(timeout);
    });
  }

  stop(): void {
    this.clearCaptureSchedule();
    this.intentionallyStopping = true;
    this.stream?.getTracks().forEach(track => track.stop());
    if (this.video) this.video.srcObject = null;
    this.stream = undefined;
    this.video = undefined;
  }

  private clearCaptureSchedule(): void {
    this.captureTimeouts.forEach(timeout => window.clearTimeout(timeout));
    this.captureTimeouts = [];
  }

  private async captureAndUpload(
    userId: string,
    sessionId: string,
    captureNumber: number,
    minute: number
  ): Promise<void> {
    try {
      const file = await this.captureFrame(captureNumber, minute);

      // Keep camera enforcement and capture scheduling active, but do not send
      // captured images anywhere until assessment storage is private.
      if (!environment.assessmentProctoring.uploadCaptures) return;

      const objectBase = buildAssessmentCaptureObjectBase(
        userId,
        sessionId,
        captureNumber,
        minute,
        new Date()
      );

      this.uploadService.getUploadURL(objectBase, 'jpeg').pipe(
        map(response => this.extractUploadUrl(response)),
        switchMap(uploadUrl => this.uploadService.postPrivateImageOnS3(uploadUrl, file)),
        retry({ count: 2, delay: 2000 })
      ).subscribe({
        error: error => {
          console.error('Assessment camera capture upload failed', error);
          this.uploadFailed$.next(error);
        }
      });
    } catch (error) {
      console.error('Assessment camera capture failed', error);
      this.uploadFailed$.next(error);
    }
  }

  private captureFrame(captureNumber: number, minute: number): Promise<File> {
    const video = this.video;
    if (!video || !this.isCameraActive || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return Promise.reject(new Error('Camera is not ready for capture.'));
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return Promise.reject(new Error('Unable to create camera capture.'));
    context.drawImage(video, 0, 0, width, height);

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('Unable to encode camera capture.'));
          return;
        }
        resolve(new File(
          [blob],
          `image_${String(captureNumber).padStart(2, '0')}_minute_${String(minute).padStart(2, '0')}.jpeg`,
          { type: 'image/jpeg', lastModified: Date.now() }
        ));
      }, 'image/jpeg', 0.85);
    });
  }

  private extractUploadUrl(response: any): string {
    let payload = response?.body ?? response;
    if (typeof payload === 'string') payload = JSON.parse(payload);
    if (!payload?.uploadURL) throw new Error('Upload URL was not returned.');
    return payload.uploadURL;
  }
}
